<template>
  <div id="app" :class="{ 'app--with-tabbar': showTabbar }">
    <template v-if="isConfigLoaded">
      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>

      <Teleport to="body">
        <van-tabbar
          v-if="showTabbar"
          route
          fixed
          class="custom-tabbar"
          style="z-index: 99"
        >
          <van-tabbar-item
            v-for="(item, index) in menuList"
            :key="index"
            :to="item.path"
            :icon="item.icon"
            >{{ item.name }}</van-tabbar-item
          >
        </van-tabbar>
      </Teleport>
    </template>
    <div v-else class="loading-container">
      <van-loading type="spinner" color="#1989fa" size="24px" vertical>
        加载中...
      </van-loading>
    </div>
  </div>
</template>

<script setup>
import { computed, provide, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

const store = useStore();
const route = useRoute();

const overlayOpenCount = ref(0);

const setAppOverlayOpen = (open) => {
  if (open) {
    overlayOpenCount.value += 1;
  } else {
    overlayOpenCount.value = Math.max(0, overlayOpenCount.value - 1);
  }
};

provide("setAppOverlayOpen", setAppOverlayOpen);

// 判断配置是否加载完成
const isConfigLoaded = computed(() => {
  return !!store.state.config.webViewConfig;
});

const menuList = computed(() => {
  const config = store.state.config.webViewConfig;
  const list = [...(config?.page_menu_list || [])];
  // 房产为一级页：配置里没有时自动插入到首页后
  if (!list.some((item) => item.path === "/house")) {
    const houseItem = { name: "房产", path: "/house", icon: "hotel-o" };
    const homeIdx = list.findIndex(
      (item) => item.path === "/" || item.path === "/home"
    );
    if (homeIdx >= 0) {
      list.splice(homeIdx + 1, 0, houseItem);
    } else {
      list.unshift(houseItem);
    }
  }
  return list;
});

// 判断当前页面是否应该显示底部菜单
const shouldShowTabbar = computed(() => {
  if (route.meta?.hideTabbar) return false;

  // 如果menuList中包含当前路径，则显示底部菜单
  const currentPath = route.path;

  // 检查当前路径是否在菜单列表中
  const inMenuList = menuList.value.some((item) => item.path === currentPath);

  // 特殊情况处理：如果是二级页面，可能需要隐藏底部菜单
  // 例如：/address/edit/1 这样的路径不应该显示底部菜单
  const isSubPage = currentPath.split("/").length > 2;

  return inMenuList && !isSubPage;
});

const showTabbar = computed(
  () => shouldShowTabbar.value && overlayOpenCount.value === 0
);
</script>

<style>
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

#app.app--with-tabbar {
  padding-bottom: 58px;
}

.custom-tabbar .van-tabbar-item {
  color: #333; /* 默认字体颜色 */
}

.custom-tabbar .van-tabbar-item--active {
  color: #d81e06; /* 活动项的金色字体 */
}

/* 覆盖 WebView 注入的 bottom 偏移，强制贴底 */
html body .van-tabbar.custom-tabbar {
  position: fixed !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  transform: none !important;
  margin: 0 !important;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}
</style>
